/*
* Report page
*/

import * as _ from 'lodash';
import moment from 'moment';
import * as React from 'react';
import { Table } from 'react-bootstrap';
import ReactImageFallback from 'react-image-fallback';
import stringtemplate from 'string-template';

import Branding from '../../../branding/branding';
import { Formatters } from '../../../common/formatters';
import ApiEndpoints from '../../../config/apiendpoints';
import * as OrderCommon from '../../order-common';
import * as BidLetterReportComponents from '../report-components';

export const Render = ({ reportData }) => {
    const { currentOrder, branding, association, associationClient, orderClient, pricingMatrix, orderServicesPricingMatrix, serviceLevels, orderManagerUser, bidLetterTextContents, twoOptionPricing } = reportData;
    const pmL2 = _.find(pricingMatrix, { 'level': '2' });
    const pmL3 = _.find(pricingMatrix, { 'level': '3' });
    const pmL5 = _.find(pricingMatrix, { 'level': '5' });

    const calcL45WeekTurnaround = Formatters.getFloat(pmL2, 'standardPrice') - Formatters.getFloat(pmL2, 'economyPrice') + Formatters.getFloat(pmL5, 'economyPrice');
    const calcL42WeekTurnaround = Formatters.getFloat(pmL2, 'rushPrice') - Formatters.getFloat(pmL2, 'standardPrice') + Formatters.getFloat(pmL5, 'economyPrice');
    const calcL45WeekTurnaroundFinancialUpdate = Formatters.getFloat(pmL3, 'standardPrice') - Formatters.getFloat(pmL3, 'economyPrice') + Formatters.getFloat(pmL5, 'economyPrice');
    const calcL42WeekTurnaroundFinancialUpdate = Formatters.getFloat(pmL3, 'rushPrice') - Formatters.getFloat(pmL3, 'standardPrice') + Formatters.getFloat(pmL5, 'economyPrice');

    const ospmEconomy = _.get(orderServicesPricingMatrix, 'economyPricing', {});
    const ospmStandard = _.get(orderServicesPricingMatrix, 'standardPricing', {});
    const ospmRush = _.get(orderServicesPricingMatrix, 'RushPricing', {});

    let bidLetterData = {
        COMPANY_EMAIL_ADDRESS: '',
        LETTER_SEND_DATE: '',

        ASSOCIATION_ID: Formatters.getStr(currentOrder, 'associationId'),
        ASSOCIATION_NAME: Formatters.getStr(association, 'name'),
        ORDER_CLIENT_CONTACT_NAME: Formatters.getStr(currentOrder, 'orderClientContactLetter'),
        ASSOCIATION_CLIENT_NAME: Formatters.getStr(currentOrder, 'association.client.name'),
        ASSOCIATION_CLIENT_LOCATION_ADDRESS_1: Formatters.getStr(currentOrder, 'association.client.location.address1'),
        ASSOCIATION_CLIENT_LOCATION_CITY_STATE_ZIP: Formatters.getStr(currentOrder, 'orderClientAddressCityStateZip'),
        LETTER_SALUTATION: Formatters.getStr(currentOrder, 'orderClientContactLetterSaluatation'),

        TABLE_FOOTNOTE_LINE_1_ALL_STATES_EXCEPT_UT: `* Previous study does not have to have been completed by ${Formatters.getStr(currentOrder, 'association.company.name')}`,
        TABLE_FOOTNOTE_LINE_1_STATE_UT: `* Level I &amp; II studies meet UT Code 57-8a-211 &amp; 57-8-7.5 for a Reserve Analysis to be performed at least every 6 years.  All levels of service meet the requirement for an update at least every 3 years.`,
        TABLE_FOOTNOTE_LINE_2_STATE_CA: (twoOptionPricing) ? `* Meets CA Civil Code §5550 for a study with site visit at least once every 3 years` : `** Meets CA Civil Code §5550 for a study with site visit at least once every 3 years`,
        TABLE_FOOTNOTE_LINE_2_STATE_NV: (twoOptionPricing) ? `* Meets NRS 116.31152 requirements for a Full reserve study.` : `** Meets NRS 116.31152 requirements for a Full reserve study.`,
        TABLE_FOOTNOTE_LINE_3_ALL_STATES: `*** 5-Week Turnaround for site visit year is ` + Formatters.formatCurrencyIntl(calcL45WeekTurnaround, 0) +
            `, 2-weeks is ` + Formatters.formatCurrencyIntl(calcL42WeekTurnaround, 0) + // $####` +
            `. 5-Week turnaround for any Financial Update year is ` + Formatters.formatCurrencyIntl(calcL45WeekTurnaroundFinancialUpdate, 0) + // $####` +
            ` Dollars, 2-weeks is ` + Formatters.formatCurrencyIntl(calcL42WeekTurnaroundFinancialUpdate, 0) + '.', // $####.`,
        LETTER_SIGNATURE_MANAGER_NAME: Formatters.composeUsernameLabel(orderManagerUser, false),
        LETTER_SIGNATURE_MANAGER_TITLE: Formatters.getStr(orderManagerUser, 'contact.title'),
        LETTER_SIGNATURE_COMPANY_NAME: Formatters.getStr(currentOrder, 'association.company.name'),
    };

    let textContent = _.get(bidLetterTextContents, 'contents', {});

    const headerRow = () => {
        const slEconomy = Formatters.getStr(_.find(serviceLevels, { 'serviceLevelId': 5 }), 'timeframe');
        const slStandard = Formatters.getStr(_.find(serviceLevels, { 'serviceLevelId': 1 }), 'timeframe');
        const slRush = Formatters.getStr(_.find(serviceLevels, { 'serviceLevelId': 11 }), 'timeframe');

        return (
            <tr className="order-bid-letter-pdf-cover-page-service-levels-table-row">
                <th colSpan={2} className="text-center order-bid-letter-pdf-cover-page-service-levels-table-header">Level{currentOrder.isPreliminaryStudy ? '' : 's'} Of Service</th>
                <th className="text-center order-bid-letter-pdf-cover-page-service-levels-table-header">{`${slEconomy} Turnaround`}</th>
                <th className="text-center order-bid-letter-pdf-cover-page-service-levels-table-header">{`${slStandard} Turnaround`}</th>
                <th className="text-center order-bid-letter-pdf-cover-page-service-levels-table-header">{`${slRush} Turnaround`}</th>
            </tr>
        );
    };

    const orderClientStateCode = Formatters.getStr(currentOrder, 'association.client.location.stateCode');

    let rows = _.map(
        _.filter(pricingMatrix, (c) => {
            return (
                (Formatters.getStr(c, 'level') === '1' && !twoOptionPricing) ||
                Formatters.getStr(c, 'level') === '2' ||
                Formatters.getStr(c, 'level') === '3');
        }),
        (o, i) => {
            const sl = _.find(serviceLevels, { 'report': `Level ${Formatters.getInt(o, 'level')}` });
            let slName = (twoOptionPricing) ?
                ((Formatters.getStr(sl, 'report') === 'Level 1' || Formatters.getStr(sl, 'report') === 'Level 2') ? `Full Study` :
                    ((Formatters.getStr(sl, 'report') === 'Level 3') ? `Financial Update` : Formatters.getStr(sl, 'report'))) :
                Formatters.getStr(sl, 'report');

            if (orderClientStateCode === OrderCommon.BidLetterStateCodes.Nevada || orderClientStateCode === OrderCommon.BidLetterStateCodes.California) {
                if (twoOptionPricing) {
                    switch (slName) {
                        case 'Full Study':
                            slName += '*';
                            break;
                        case 'Financial Update':
                            break;
                        default:
                            break;
                    }
                } else {
                    if (!currentOrder.isPreliminaryStudy) {
                        switch (Formatters.getStr(sl, 'report')) {
                            case 'Level 1':
                                slName += '**';
                                break;

                            case 'Level 2':
                                slName += '**';
                                break;

                            default:
                                break;
                        }
                    }
                }
            }

            let slDescriptionHtml = Formatters.getStr(sl, 'serviceLevelList');
            if (currentOrder.isPreliminaryStudy) {
                slDescriptionHtml = slDescriptionHtml.replace(/<b>(.*?)<\/b>/, '<b>Preliminary Study</b>');
            }
            if (twoOptionPricing) {
                if ((Formatters.getStr(sl, 'report') === 'Level 1') || (Formatters.getStr(sl, 'report') === 'Level 2')) {
                    slDescriptionHtml = slDescriptionHtml.replace(/<b>(.*?)<\/b>/, '<b>Site Visit Reserve Study</b>');
                    slDescriptionHtml = slDescriptionHtml.replace(/<li>Update component list with life and cost estimates<\/li>/, '<li>Prepare component list with life and cost estimates</li>');
                }
                if ((Formatters.getStr(sl, 'report') === 'Level 3')) {
                    slDescriptionHtml = slDescriptionHtml.replace(/<b>(.*?)<\/b>/, '<b>Financial Update Study</b>');
                }
            }

            return (
                <tr key={`pricing-matrix-item-${i}`} className="order-bid-letter-pdf-cover-page-service-levels-table-row">
                    <td className="col-xs-2 order-bid-letter-pdf-cover-page-service-levels-table-cell order-bid-letter-pdf-service-name">
                        {currentOrder.isPreliminaryStudy ? 'Preliminary Study' : slName}
                    </td>
                    <td className="col-xs-5 order-bid-letter-pdf-cover-page-service-levels-table-cell">
                        <div dangerouslySetInnerHTML={{ __html: slDescriptionHtml }} />
                    </td>
                    <td className="text-center order-bid-letter-pdf-cover-page-service-levels-table-cell order-bid-letter-pdf-service-price"><BidLetterReportComponents.CurrencyNAZeroValue value={Formatters.getFloat(o, 'economyPrice')} /></td>
                    <td className="text-center order-bid-letter-pdf-cover-page-service-levels-table-cell order-bid-letter-pdf-service-price"><BidLetterReportComponents.CurrencyNAZeroValue value={Formatters.getFloat(o, 'standardPrice')} /></td>
                    <td className="text-center order-bid-letter-pdf-cover-page-service-levels-table-cell order-bid-letter-pdf-service-price"><BidLetterReportComponents.CurrencyNAZeroValue value={Formatters.getFloat(o, 'rushPrice')} /></td>
                </tr>
            );
        }
    );

    if (currentOrder.isPreliminaryStudy && rows.length > 0) {
        rows = [rows[0]];
    }

    const level4Rows = () => {
        const oPossible = _.find(pricingMatrix, { 'level': '5' });
        const oEconomy = _.get(oPossible, 'economyPrice');
        const oStandard = _.get(oPossible, 'standardPrice');
        const oRush = _.get(oPossible, 'rushPrice');
        const ospmEcon = _.get(ospmEconomy, 'price');
        const ospmStd = _.get(ospmStandard, 'price');
        const ospmRsh = _.get(ospmRush, 'price');
        let bestPrices = { level: '5', economyPrice: 0, standardPrice: 0, rushPrice: 0 };
        if (ospmEcon && ospmEcon !== oEconomy) {
            bestPrices.economyPrice = ospmEcon;
        } else {
            bestPrices.economyPrice = oEconomy;
        }
        if (ospmStd && ospmStd !== oStandard) {
            bestPrices.standardPrice = ospmStd;
        } else {
            bestPrices.standardPrice = oStandard;
        }
        if (ospmRsh && ospmRsh !== oRush) {
            bestPrices.rushPrice = ospmRsh;
        } else {
            bestPrices.rushPrice = oRush;
        }
        const o = bestPrices;

        const slActual = _.find(serviceLevels, { 'serviceLevelId': 15 });
        const slLevel4 = _.find(serviceLevels, { 'serviceLevelId': 13 });
        let slName = Formatters.getStr(slLevel4, 'name');

        if (orderClientStateCode === OrderCommon.BidLetterStateCodes.Nevada || orderClientStateCode === OrderCommon.BidLetterStateCodes.California) {
            slName += '**';
        }

        const slDescriptionHtml = Formatters.getStr(slActual, 'serviceLevelList');

        return (
            <tr key={`pricing-matrix-item-sl-15`} className="order-bid-letter-pdf-cover-page-service-levels-table-row">
                <td className="col-xs-2 order-bid-letter-pdf-cover-page-service-levels-table-cell order-bid-letter-pdf-service-name">
                    {slName}
                </td>
                <td className="col-xs-5 order-bid-letter-pdf-cover-page-service-levels-table-cell">
                    <div dangerouslySetInnerHTML={{ __html: slDescriptionHtml }} />
                </td>
                <td className="text-center order-bid-letter-pdf-cover-page-service-levels-table-cell order-bid-letter-pdf-service-price">
                    <BidLetterReportComponents.CurrencyNAZeroValue value={Formatters.getFloat(o, 'economyPrice')} />
                    <span>***</span>
                </td>
                <td colSpan={2} className="text-center order-bid-letter-pdf-cover-page-service-levels-table-cell order-bid-letter-pdf-service-price">
                    Flat annual fee!
                </td>
            </tr>
        );
    };

    const companyEmailAddress = `${(orderClientStateCode === OrderCommon.BidLetterStateCodes.Utah) ? 'sales' : 'admin'}${Formatters.getStr(currentOrder, 'association.company.emailDomain')}`;

    const orderAssocCompanyBrandKey = Formatters.getStr(currentOrder, 'company.brandKey');

    // Header image
    let headerPath = `/Branding/${orderAssocCompanyBrandKey}/letterheads/bid-letter-header.png`;
    let headerNotFoundPath = `/Branding/${orderAssocCompanyBrandKey}/letterheads/bid-letter-header.png`;

    switch (orderClientStateCode) {
        case OrderCommon.BidLetterStateCodes.Nevada:
            headerPath = `/Branding/${orderAssocCompanyBrandKey}/letterheads/bid-letter-header.png`;
            break;

        default:
            headerPath = `/Branding/${orderAssocCompanyBrandKey}/letterheads/bid-letter-header-other.png`;
            break;
    }

    const headerImageObj = {
        src: ApiEndpoints.getBidLetterUserSignaturePictureUrl(headerPath, false),
        fallbackSrc: ApiEndpoints.getBidLetterUserSignaturePictureUrl(headerNotFoundPath, false),
    };

    // Signature
    const signaturePath = Branding.getBrandUserSignaturePicture(Formatters.getStr(orderManagerUser, 'signaturePictureFilename'), orderAssocCompanyBrandKey);
    const signatureNotFoundPath = Branding.getBrandUserSignaturePicture(Branding.appBrandImageFileNames.signatureNotFound, orderAssocCompanyBrandKey);

    const signatureImageObj = {
        src: ApiEndpoints.getBidLetterUserSignaturePictureUrl(signaturePath, true),
        fallbackSrc: ApiEndpoints.getBidLetterUserSignaturePictureUrl(signatureNotFoundPath, true),
    };

    // Footer image
    let footerPath = `/Branding/${orderAssocCompanyBrandKey}/letterheads/bid-letter-footer.png`;
    let footerNotFoundPath = `/Branding/${orderAssocCompanyBrandKey}/letterheads/bid-letter-footer.png`;

    /* switch (orderClientStateCode) {
        case OrderCommon.BidLetterStateCodes.Utah:
            footerPath = `/Branding/${orderAssocCompanyBrandKey}/letterheads/bid-letter-footer-UT.png`;
            break;

        default:
            footerPath = `/Branding/${orderAssocCompanyBrandKey}/letterheads/bid-letter-footer.png`;
            break;
    } */

    const footerImageObj = {
        src: ApiEndpoints.getBidLetterUserSignaturePictureUrl(footerPath, false),
        fallbackSrc: ApiEndpoints.getBidLetterUserSignaturePictureUrl(footerNotFoundPath, false),
    };

    bidLetterData = _.merge(bidLetterData, {
        COMPANY_EMAIL_ADDRESS: companyEmailAddress,
        LETTER_SEND_DATE: moment(new Date()).format('D MMM YYYY'),
    });

    const order3YearPlanLanguage = _.get(currentOrder, 'bidLetter3YearPlanLanguage', true);
    const show3yearPlanLanguage = (order3YearPlanLanguage) ? (orderClientStateCode === OrderCommon.BidLetterStateCodes.California || Formatters.getInt(association, 'company.companyId') === 2) : false;

    const letterTable = (
        <Table condensed={false} bordered={false} responsive={false} className="order-bid-letter-pdf-cover-page-service-levels-table">
            <thead>
                {headerRow()}
            </thead>
            <tbody>
                {rows}
            </tbody>

            {
                (show3yearPlanLanguage) ? (
                    [(<tbody key="tbody-1" className="order-bid-letter-pdf-cover-page-service-levels-table-body-break">
                        <tr>
                            <td colSpan={5} />
                        </tr>
                    </tbody>),

                    (<tbody key="tbody-2">
                        {level4Rows()}
                    </tbody>
                    )]
                ) : (<tbody />)
            }
        </Table>
    );

    const bidLetterIntro = stringtemplate(Formatters.getStr(textContent, 'letterIntro'), bidLetterData);
    const bidLetterConclusion = stringtemplate(Formatters.getStr(textContent, 'letterConclusion'), bidLetterData);
    const bidLetterSignature = stringtemplate(Formatters.getStr(textContent, 'letterSignature'), bidLetterData);

    const letterTableFootnote = currentOrder.isPreliminaryStudy ? null : (
        <div className="order-bid-letter-pdf-cover-page-service-levels-table-footer-wrapper">
            {(twoOptionPricing) ? '' : (orderClientStateCode !== OrderCommon.BidLetterStateCodes.Utah) ? (
                <p>{bidLetterData.TABLE_FOOTNOTE_LINE_1_ALL_STATES_EXCEPT_UT}</p>
            ) : (
                <p>{bidLetterData.TABLE_FOOTNOTE_LINE_1_STATE_UT}</p>
            )}

            {(orderClientStateCode === OrderCommon.BidLetterStateCodes.California) ? (
                <p>{bidLetterData.TABLE_FOOTNOTE_LINE_2_STATE_CA}</p>
            ) : (<span />)
            }

            {(orderClientStateCode === OrderCommon.BidLetterStateCodes.Nevada) ? (
                <p>{bidLetterData.TABLE_FOOTNOTE_LINE_2_STATE_NV}</p>
            ) : (<span />)
            }

            <p>{(show3yearPlanLanguage) ? bidLetterData.TABLE_FOOTNOTE_LINE_3_ALL_STATES : ''}</p>
        </div>
    );

    return (
        <div>
            <BidLetterReportComponents.ReportPage className="pdf-order-bid-letter-cover-page" reportData={reportData}>
                <div>
                    <ReactImageFallback
                        src={headerImageObj.src}
                        fallbackImage={headerImageObj.fallbackSrc}
                        className="pdf-order-bid-letter-header-image"
                    />
                </div>

                <div className="text-left pdf-order-bid-letter-pdf-letter-body" dangerouslySetInnerHTML={{ __html: bidLetterIntro }} />

                <div className="text-left pdf-order-bid-letter-pdf-letter-body">
                    <div className="order-bid-letter-pdf-cover-page-service-levels-table-wrapper">
                        {letterTable}
                        {letterTableFootnote}
                    </div>
                </div>

                {(twoOptionPricing) ? (
                    <div className="text-left pdf-order-bid-letter-pdf-letter-body">
                        <br />
                    </div>
                ) : null}

                <div className="text-left pdf-order-bid-letter-pdf-letter-body" dangerouslySetInnerHTML={{ __html: bidLetterConclusion }} />

                <div className="text-left pdf-order-bid-letter-pdf-letter-body">
                    <ReactImageFallback
                        src={signatureImageObj.src}
                        fallbackImage={signatureImageObj.fallbackSrc}
                        className="pdf-order-bid-letter-signature-image"
                    />
                </div>

                <div className="text-left pdf-order-bid-letter-pdf-letter-body" dangerouslySetInnerHTML={{ __html: bidLetterSignature }} />

                <div className="pdf-order-bid-letter-footer-container">
                    <ReactImageFallback
                        src={footerImageObj.src}
                        fallbackImage={footerImageObj.fallbackSrc}
                        className="pdf-order-bid-letter-footer-image"
                    />
                </div>
            </BidLetterReportComponents.ReportPage>
        </div>
    );
};



// WEBPACK FOOTER //
// ./src/order/bid-letter/pages/report-page-cover.tsx